import { useEffect, useRef, useState } from "react";
import { mseStreamUrl, type NtStream } from "../lib/cctvPlayer";

/**
 * Live NT camera video, played directly from the NVR's MSE WebSocket.
 *
 * Why not just embed the vendor's player page: its <video> is unmuted, so
 * Chrome's autoplay policy leaves it paused at 0:00 behind a play button in
 * any profile that hasn't built up Media Engagement on that origin. The
 * operator sees a black rectangle and concludes the camera is broken. Owning
 * the element lets us set muted + playsInline + autoplay, which always
 * starts. It also skips ~600 KB of vendor page for every camera click.
 *
 * Wire protocol (mirrors the vendor's own startPlay(), which is the only
 * documentation that exists): binary WebSocket; the first frame whose byte 0
 * is 9 carries the codec string as UTF-8 for
 * addSourceBuffer('video/mp4; codecs="…"'); every later frame is a
 * fragmented-MP4 segment. Segments must be appended one at a time — SourceBuffer
 * rejects an append while updating — so they queue and drain on 'updateend'.
 */

type Phase = "connecting" | "playing" | "error";

interface Props {
  stream: NtStream;
  /** Camera name, for the accessible label. */
  name: string;
  /**
   * Show the native control bar. True for the lightbox (the operator drives
   * volume/fullscreen there). Must be false when the player is embedded in a
   * button: the UA control bar is itself interactive, so it swallows clicks
   * across the whole element and adds 5 unlabelled tab stops to a tile that
   * is meant to be one button.
   */
  controls?: boolean;
}

export function CctvMsePlayer({ stream, name, controls = true }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [phase, setPhase] = useState<Phase>("connecting");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (typeof MediaSource === "undefined") {
      setPhase("error");
      return;
    }

    setPhase("connecting");

    const url = mseStreamUrl(stream);
    const mediaSource = new MediaSource();
    const objectUrl = URL.createObjectURL(mediaSource);
    video.src = objectUrl;

    let ws: WebSocket | null = null;
    let sourceBuffer: SourceBuffer | null = null;
    let retryTimer: number | null = null;
    let firstFrameTimer: number | null = null;
    let retryScheduled = false;
    const queue: ArrayBuffer[] = [];
    let disposed = false;

    const failOrRetry = () => {
      if (disposed || retryScheduled) return;
      if (attempt < 2) {
        retryScheduled = true;
        setPhase("connecting");
        retryTimer = window.setTimeout(() => {
          retryScheduled = false;
          setAttempt((value) => value + 1);
        }, 1_200);
      } else {
        setPhase("error");
      }
    };

    // One rule: segments only ever enter through the queue, and only drain()
    // appends. The vendor's page tracks an extra "streaming" flag and appends
    // the first segment straight from onmessage; replicating that deadlocked
    // here — the flag went false while a later append was still in flight and
    // the pipeline stopped feeding the buffer, freezing playback ~2s in.
    const drain = () => {
      if (!sourceBuffer || sourceBuffer.updating || queue.length === 0) return;
      const next = queue.shift()!;
      try {
        sourceBuffer.appendBuffer(next);
      } catch {
        setPhase("error");
      }
    };

    const onSourceOpen = () => {
      if (disposed) return;
      ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";

      ws.onmessage = (event) => {
        if (disposed) return;
        const buf = event.data as ArrayBuffer;
        const head = new Uint8Array(buf, 0, 1);

        // Byte 0 === 9 marks the codec announcement, not media.
        if (head[0] === 9 && !sourceBuffer) {
          const codec = new TextDecoder("utf-8").decode(new Uint8Array(buf).slice(1));
          const mime = `video/mp4; codecs="${codec}"`;
          try {
            if (!MediaSource.isTypeSupported(mime)) throw new Error(`Unsupported codec: ${codec}`);
            sourceBuffer = mediaSource.addSourceBuffer(mime);
            sourceBuffer.mode = "segments";
            sourceBuffer.addEventListener("updateend", drain);
            sourceBuffer.addEventListener("error", failOrRetry);
          } catch {
            failOrRetry();
          }
          return;
        }

        if (!sourceBuffer) return;
        queue.push(buf);
        drain();
      };

      ws.onerror = failOrRetry;
      ws.onclose = failOrRetry;
    };

    mediaSource.addEventListener("sourceopen", onSourceOpen);

    const onLoadedData = () => {
      if (firstFrameTimer != null) window.clearTimeout(firstFrameTimer);
      setPhase("playing");
      // Muted, so this is allowed without a user gesture.
      void video.play().catch(() => undefined);
    };
    video.addEventListener("loadeddata", onLoadedData);
    firstFrameTimer = window.setTimeout(failOrRetry, 15_000);

    // Watchdog. A live MSE feed stalls for ordinary reasons — a late segment,
    // a backgrounded tab, the decoder pausing at the end of what is buffered.
    // Rather than react to one 'pause' event (which fires before the buffer
    // has grown, so there is nowhere to seek to yet), check periodically and
    // jump back to the live edge whenever playback has fallen behind it.
    const watchdog = window.setInterval(() => {
      if (disposed || video.buffered.length === 0) return;
      const live = video.buffered.end(video.buffered.length - 1);
      if (live - video.currentTime > 1.5) video.currentTime = live - 0.1;
      if (video.paused) void video.play().catch(() => undefined);
    }, 2000);

    return () => {
      disposed = true;
      window.clearInterval(watchdog);
      if (retryTimer != null) window.clearTimeout(retryTimer);
      if (firstFrameTimer != null) window.clearTimeout(firstFrameTimer);
      video.removeEventListener("loadeddata", onLoadedData);
      mediaSource.removeEventListener("sourceopen", onSourceOpen);
      if (ws) {
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      }
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(objectUrl);
    };
  }, [stream.uuid, stream.channel, attempt]);

  return (
    <>
      <video
        ref={videoRef}
        muted
        autoPlay
        playsInline
        controls={controls}
        tabIndex={controls ? undefined : -1}
        aria-hidden={controls ? undefined : true}
        aria-label={controls ? `ภาพสดกล้อง CCTV: ${name}` : undefined}
      />
      {phase !== "playing" && (
        <p className="cctv-stream-hint cctv-stream-hint-overlay" role="status">
          {phase === "error" ? "เชื่อมต่อภาพสดไม่สำเร็จ" : "กำลังเชื่อมต่อกล้อง…"}
          {phase === "error" && (
            <button
              type="button"
              className="cctv-stream-retry"
              onClick={() => {
                setPhase("connecting");
                setAttempt(0);
              }}
            >
              ลองใหม่
            </button>
          )}
        </p>
      )}
    </>
  );
}
